import { STORAGE_ERROR_CODE, STORAGE_ERROR_MESSAGES } from "./constants"

import type { StorageErrorCode } from "../../types"

const KEY_CHAR_PATTERN = /^[A-Za-z0-9_-]+$/
const KEY_MAX_LENGTH = 128

export type KnownStorageErrorCode = keyof typeof STORAGE_ERROR_MESSAGES
export type KeyValidationErrorCode =
  | typeof STORAGE_ERROR_CODE.STORAGE_KEY_REQUIRED
  | typeof STORAGE_ERROR_CODE.STORAGE_KEY_TOO_LONG
  | typeof STORAGE_ERROR_CODE.STORAGE_KEY_INVALID

/**
 * Validates the key against Telegram's documented storage rules and returns the
 * specific TG error code the server would have raised. `null` means the key is
 * valid. Narrowed to known codes so the value flows into `notifyError` /
 * `logStorageError` without a cast.
 */
export const validateKey = (key: string): KeyValidationErrorCode | null => {
  if (key === "") return STORAGE_ERROR_CODE.STORAGE_KEY_REQUIRED
  if (key.length > KEY_MAX_LENGTH) return STORAGE_ERROR_CODE.STORAGE_KEY_TOO_LONG
  if (!KEY_CHAR_PATTERN.test(key)) return STORAGE_ERROR_CODE.STORAGE_KEY_INVALID
  return null
}

export const serialize = (data: unknown): string => {
  let result: string | undefined
  try {
    result = JSON.stringify(data)
  } catch {
    throw new Error(STORAGE_ERROR_CODE.SERIALIZATION_ERROR)
  }
  if (typeof result !== "string") {
    // JSON.stringify silently returns undefined for `undefined`, functions, etc.
    throw new Error(STORAGE_ERROR_CODE.SERIALIZATION_ERROR)
  }
  return result
}

export const deserialize = (data: string): unknown => {
  try {
    return JSON.parse(data)
  } catch {
    throw new Error(STORAGE_ERROR_CODE.DESERIALIZATION_ERROR)
  }
}

const isKnownStorageErrorCode = (code: string): code is KnownStorageErrorCode =>
  Object.hasOwn(STORAGE_ERROR_MESSAGES, code)

export const getStorageErrorMessage = (code: StorageErrorCode): { key: string; fallback: string } | null =>
  isKnownStorageErrorCode(code) ? STORAGE_ERROR_MESSAGES[code] : null

export interface ResolvedStorageError {
  /** Known storage error code or unrecognized string forwarded as-is. */
  code: StorageErrorCode
  /** User-facing English fallback resolved from the constants table when the code is known, otherwise the raw string. */
  message: string
}

/**
 * Normalizes an arbitrary thrown/rejected value into `{ code, message }` so
 * the controller can both log a human-readable line AND hand a structured
 * payload to a per-call `onError` callback without each path re-implementing
 * the shape resolution. Known codes (`STORAGE_ERROR_CODE.*`) resolve to their
 * fallback text; unknown strings pass through unchanged; non-Error / non-string
 * values fall back to `UNKNOWN_ERROR`.
 */
export const resolveStorageError = (error: unknown): ResolvedStorageError => {
  const raw =
    error instanceof Error ? error.message : typeof error === "string" ? error : STORAGE_ERROR_CODE.UNKNOWN_ERROR
  const known = getStorageErrorMessage(raw)
  return { code: raw, message: known ? known.fallback : raw }
}

/**
 * Logs any error — Error instance, plain string, or arbitrary value — through
 * `resolveStorageError` so the printed line always matches what an `onError`
 * callback would receive. Use a literal from `STORAGE_ERROR_CODE` (constants.ts)
 * when the code is known statically so call sites can't drift away from the
 * constants table.
 */
export const logStorageError = (context: string, error: unknown): void => {
  console.error(context, resolveStorageError(error).message)
}

/**
 * Per-call observability hooks for any controller method. Pass `{ onSuccess,
 * onError }` as the last argument when a single call site wants to react to
 * the outcome (toast, haptic, redirect). Both are optional — call sites that
 * don't pass anything keep the existing fire-and-forget contract. Errors are
 * still logged internally regardless of whether `onError` was supplied, so
 * adding a callback never silences the console signal.
 */
export interface StorageOperationCallbacks {
  onSuccess?: () => void
  onError?: (error: ResolvedStorageError) => void
}

/**
 * Logs via `logStorageError` AND forwards the resolved `{ code, message }`
 * payload to `callbacks.onError` when supplied. Single helper so every
 * controller method follows the same "log first, then notify" sequence.
 */
export const notifyError = (context: string, error: unknown, callbacks?: StorageOperationCallbacks): void => {
  logStorageError(context, error)
  if (callbacks?.onError) callbacks.onError(resolveStorageError(error))
}
